import { Box, Button, Flex, Input, Link, Stack, Text } from '@chakra-ui/react'
import { useState } from 'react'

import { ActionButton } from '../../../components/ActionButton'
import { ConfirmDialog } from '../../../components/ConfirmDialog'
import { FileInput } from '../../../components/FileInput'
import { FormField } from '../../../components/FormField'
import { Icon } from '../../../components/Icon'
import { SectionTitle } from '../../../components/SectionCard'
import { getApiErrorMessage, useAppToast } from '../../../helpers'
import { EmployeeService } from '../../../services/Employee/EmployeeService'
import { IEmployeeDetailRaw, IEmployeeDocumentRaw } from '../../../services/Employee/types'

interface IEmployeeDocumentsTabProps {
  detail: IEmployeeDetailRaw
  canEdit: boolean
  /** Called after a file is sent or removed, to read the sheet again. */
  onChanged: () => void
}

const service = new EmployeeService()

function DocumentLink({ document }: { document: IEmployeeDocumentRaw }) {
  return (
    <Link href={document.url} isExternal color="magenta.600" display="inline-flex" alignItems="center" gap={1}>
      <Icon name="lni lni-download" />
      {document.original_name}
    </Link>
  )
}

/**
 * The documents the role asks for (sent or not) and the others: loose ones RH added and those of a
 * previous role. Each file is sent on its own, right away.
 */
export default function EmployeeDocumentsTab({ detail, canEdit, onChanged }: IEmployeeDocumentsTabProps) {
  const toast = useAppToast()
  const [busy, setBusy] = useState<string | null>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [looseName, setLooseName] = useState('')
  const [looseFile, setLooseFile] = useState<File | null>(null)
  const [removing, setRemoving] = useState<IEmployeeDocumentRaw | null>(null)

  const upload = (key: string, file: File, target: { requirementId: number } | { name: string }) => {
    setBusy(key)
    setErrors((current) => ({ ...current, [key]: '' }))
    return service
      .uploadDocument(detail.id, file, target)
      .then(() => {
        toast.success('Documento enviado')
        onChanged()
        return true
      })
      .catch((error) => {
        setErrors((current) => ({
          ...current,
          [key]: getApiErrorMessage(error) ?? 'Não foi possível enviar o arquivo.'
        }))
        return false
      })
      .finally(() => setBusy(null))
  }

  const addLoose = () => {
    if (!looseFile) return
    if (!looseName.trim()) {
      setErrors((current) => ({ ...current, loose: 'Informe o nome do documento.' }))
      return
    }
    upload('loose', looseFile, { name: looseName.trim() }).then((sent) => {
      if (!sent) return
      setLooseName('')
      setLooseFile(null)
    })
  }

  const remove = () => {
    if (!removing) return Promise.resolve()
    return service
      .deleteDocument(detail.id, removing.id)
      .then(() => {
        toast.success('Documento removido')
        setRemoving(null)
        onChanged()
      })
      .catch((error) => toast.error('Erro ao remover o documento', error))
  }

  return (
    <Stack spacing={8}>
      <Box>
        <SectionTitle as="h3">Documentos do cargo</SectionTitle>
        {detail.role_documents.length === 0 ? (
          <Text variant="caption">O cargo {detail.position ? `"${detail.position}" ` : ''}não pede documentos.</Text>
        ) : (
          <Stack spacing={5}>
            {detail.role_documents.map(({ requirement, document }) => {
              const key = `role-${requirement.id}`
              return canEdit ? (
                <FileInput
                  key={key}
                  accept="admissionDocument"
                  maxSizeMB={10}
                  label={requirement.name}
                  isRequired={requirement.required}
                  value={null}
                  onChange={(file) => file && upload(key, file, { requirementId: requirement.id })}
                  existingUrl={document?.url}
                  error={errors[key] || undefined}
                  isDisabled={busy !== null}
                  helperText={document ? `Enviado: ${document.original_name}` : 'Ainda não enviado.'}
                />
              ) : (
                <Flex key={key} justify="space-between" gap={3} wrap="wrap">
                  <Box>
                    <Text fontWeight="medium">{requirement.name}</Text>
                    {requirement.required && <Text variant="caption">Obrigatório</Text>}
                  </Box>
                  {document ? <DocumentLink document={document} /> : <Text variant="caption">Ainda não enviado</Text>}
                </Flex>
              )
            })}
          </Stack>
        )}
      </Box>

      <Box>
        <SectionTitle as="h3">Outros documentos</SectionTitle>
        <Stack spacing={3}>
          {detail.other_documents.length === 0 && <Text variant="caption">Nenhum outro documento.</Text>}
          {detail.other_documents.map((document) => (
            <Flex key={document.id} justify="space-between" align="center" gap={3} wrap="wrap">
              <Box>
                <Text fontWeight="medium">{document.name}</Text>
                <DocumentLink document={document} />
              </Box>
              {canEdit && (
                <ActionButton
                  action="delete"
                  label="Remover"
                  showLabel
                  variant="ghost"
                  mr={0}
                  onClick={() => setRemoving(document)}
                  isDisabled={busy !== null}
                />
              )}
            </Flex>
          ))}

          {canEdit && (
            <Stack spacing={3} pt={2}>
              <FormField label="Nome do documento" error={errors.loose || undefined}>
                <Input
                  value={looseName}
                  onChange={(e) => setLooseName(e.target.value)}
                  placeholder="Ex.: Contrato assinado"
                  maxLength={255}
                  isDisabled={busy !== null}
                />
              </FormField>
              <FileInput
                accept="admissionDocument"
                maxSizeMB={10}
                label="Arquivo"
                value={looseFile}
                onChange={setLooseFile}
                isDisabled={busy !== null}
              />
              <Box>
                <Button
                  size="sm"
                  colorScheme="magenta"
                  leftIcon={<Icon name="lni lni-plus" />}
                  onClick={addLoose}
                  isLoading={busy === 'loose'}
                  isDisabled={!looseFile || (busy !== null && busy !== 'loose')}
                >
                  Adicionar documento
                </Button>
              </Box>
            </Stack>
          )}
        </Stack>
      </Box>

      <ConfirmDialog
        isOpen={removing !== null}
        onClose={() => setRemoving(null)}
        onConfirm={remove}
        action="delete"
        entity="documento"
        name={removing?.name}
        warning="O arquivo é apagado."
      />
    </Stack>
  )
}
